import React from 'react';
import 'ol/ol.css';
import Feature from 'ol/Feature';
import Style from 'ol/style/Style';
import { fromLonLat } from 'ol/proj';
import Icon from "ol/style/Icon";
import Point from "ol/geom/Point";
import { scaleIcon } from "Common/Map/Utilities";
import { getDetectorIcon } from "Common/Map/Utilities/Tss/DetectorUtilities";
import { Id, DetectorConfig, Link, DetectorMap } from "NSwag/ServerClient/Client.nswag";
import { TooltipProperties } from 'Common/Map/OpenLayers/Common/TooltipProperties';
import Util from 'Util/index';
import TssDetectorTooltip from './TssDetectorTooltip';

interface IProps {
    detector: DetectorConfig,
    zoomLevel: number
}

interface IState {
    icon: Icon | null,
    feature: Feature | null,
    tooltip: HTMLDivElement | null;
    imageScale: number
}

/*
 * Define the actual component, its state if it has one
 * and its logic
 */
class TssDetector extends React.Component<IProps, IState> {

    id: Id;

    /**
    * @constructor
    * @param { object } props the set of properties associated with this component
    * @return { void }
    */
    constructor(props: IProps) {
        super(props);
        this.id = props.detector.id!;
        this.state = {
            icon: null,
            feature: null,
            tooltip: null,
            imageScale: 0
        }
    }

    /**
    * Method fired when the component is mounted/initial render finishes in the DOM.
    * @return { void }
    */
    componentDidMount = () => {
        if ((this.props as any).vectorLayerSource) {
            this.addTssDetectorToVectorLayer();
        }
    }

    /**
    * Method fired when the component was updated/non-initial render finishes in the DOM.
    * @return { void }
    */
    componentDidUpdate = (prevProps: IProps, prevState: IState) => {
        if ((prevProps as any).vectorLayerSource == null && (this.props as any).vectorLayerSource) {
            this.addTssDetectorToVectorLayer();
        } else {
            // already added so update detector icon
            if (prevProps.detector.status!.opStatus !== this.props.detector.status!.opStatus) {
                this.updateDetectorIcon();
            }
        }

        if (prevState.feature != this.state.feature
            || prevProps.detector != this.props.detector) {
            this.setTooltipProperties();
        }
    }

    /**
     * Method fired before a component is unmounted or removed.
     */
    componentWillUnmount = () => {
        const { feature } = this.state;

        if (feature) {
            (this.props as any).vectorLayerSource.removeFeature(feature);
        }
    }

    /**
     * Method fired before component is re-rendered to determine if it should be re-rendered
     * @returns {boolean} - true if it should be re-rendered; false if not.
     */
    shouldComponentUpdate = (nextProps: IProps, nextState: IState) => {
        let shouldUpdate = false;

        if (nextProps.zoomLevel != this.props.zoomLevel) {
            shouldUpdate = true;
        }

        if (nextProps.detector.equipmentLocation != this.props.detector.equipmentLocation) {
            shouldUpdate = true;
        }

        if (nextProps.detector.status!.opStatus != this.props.detector.status!.opStatus) {
            shouldUpdate = true;
        }

        return shouldUpdate;
    }

    /**
    * Adds all the lanes associated with a tssDetector to the vectorLayer.
    * @return { void }
    */
    addTssDetectorToVectorLayer = () => {
        // change the projection of the coordinates to work with Open Layers
        const { equipmentLocation } = this.props.detector!;
        let formattedLocation = fromLonLat([equipmentLocation!.longitude / 1000000.0, equipmentLocation!.latitude / 1000000.0]);

        let icon = this.getDetectorIcon();

        let style = new Style({
            image: icon
        });

        let feature = new Feature(new Point(formattedLocation));
        feature.setStyle(style);

        (this.props as any).vectorLayerSource.addFeature(feature);

        this.setState({ icon, feature }, this.setTooltipProperties);
    }

    updateDetectorIcon = () => {
        const { feature } = this.state;

        let newIcon = this.getDetectorIcon();

        let newStyle = new Style({
            image: newIcon
        });

        feature!.setStyle(newStyle);

        this.setState({ icon: newIcon });
    }

    getDetectorIcon = (): Icon => {
        return new Icon({
            src: getDetectorIcon(this.props.detector.status!.opStatus),
            scale: this.state.imageScale
        });
    }

    setTooltipProperties = () => {
        const { detector } = this.props;
        const { feature } = this.state;
        if (feature) {
            let tooltipProperties = new TooltipProperties({
                id: Util.IdToString(detector.id!),
                element: this.getTooltip
            });

            feature.set("tooltipProperties", tooltipProperties);

        }
    }

    getTooltip = () => {
        const { detector } = this.props;
        return <TssDetectorTooltip
            detector={detector}
        />;
    }

   /**
   * Define how the component will render (html elements or other components)
   * @return { null } returns null to indicate that it does not render
   */
    render() {

        if(this.state.icon != null)
        {
            this.state.icon.setScale(scaleIcon(this.props.zoomLevel));
        }

        return null;
    }
}

/*
 * Make our component the default export object and attach the styles defined
 */
export default TssDetector